import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Camera, Sparkle, Image as ImageIcon, X, ArrowsClockwise, MagicWand, ArrowsLeftRight, CheckCircle } from "@phosphor-icons/react";

const BG_STYLES = [
  { id: "auto", label: "Auto" },
  { id: "studio", label: "Studio" },
  { id: "lifestyle", label: "Lifestyle" },
  { id: "marble", label: "Marble" },
  { id: "wooden", label: "Wooden" },
  { id: "gradient", label: "Gradient" },
];

const StylePicker = ({ value, onChange, disabled }) => (
  <div className="mt-3" data-testid="bg-style-picker">
    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5">AI background style</div>
    <div className="flex flex-wrap gap-1.5">
      {BG_STYLES.map((s) => (
        <button key={s.id} type="button" disabled={disabled} onClick={() => onChange(s.id)}
          className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors disabled:opacity-50 ${value === s.id ? "bg-[#15803D] text-white border-[#15803D]" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}
          data-testid={`bg-style-${s.id}`}>
          {s.label}
        </button>
      ))}
    </div>
  </div>
);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function ShopAddProduct() {
  const [imgPreview, setImgPreview] = useState(null);
  const [imgBase64, setImgBase64] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [enhanced, setEnhanced] = useState(null);  // { id, original_url, enhanced_url }
  const [usingEnhanced, setUsingEnhanced] = useState(true);
  const [showCompare, setShowCompare] = useState(false);
  const [bgStyle, setBgStyle] = useState("auto");
  const [aiHint, setAiHint] = useState("");
  const runRef = useRef(0);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: "", description: "", price: "", mrp: "", weight: "", inventory: 50,
    category_id: "", tags: [], images: []
  });
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();
  const camRef = useRef();
  const nav = useNavigate();

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);
  useEffect(() => () => { runRef.current += 1; }, []);

  const handleFile = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target.result;
      setImgPreview(dataUrl);
      setImgBase64(dataUrl);
      // AI details first so "Auto" can pick a background suited to the category
      const ai = await analyze(dataUrl);
      const hint = ai ? `${ai.category || ""} ${ai.name || ""}`.trim() : "";
      setAiHint(hint);
      enhanceImage({ image_base64: dataUrl }, bgStyle, hint);
    };
    reader.readAsDataURL(file);
  };

  const analyze = async (b64) => {
    setAnalyzing(true);
    try {
      const r = await api.post("/products/ai-analyze", { image_base64: b64 });
      setAiResult(r.data);
      const catMatch = categories.find((c) => c.name.toLowerCase().includes((r.data.category || "").toLowerCase().split(" ")[0]));
      setForm((f) => ({
        ...f,
        name: r.data.name || f.name,
        description: r.data.description || f.description,
        weight: r.data.estimated_weight || f.weight,
        price: r.data.suggested_price ? String(r.data.suggested_price) : f.price,
        tags: r.data.tags || [],
        category_id: catMatch?.id || f.category_id,
      }));
      if (r.data.ai_powered) toast.success("AI filled the details ✨");
      setAnalyzing(false);
      return r.data;
    } catch {
      toast.error("Analysis failed, fill manually");
    }
    setAnalyzing(false);
    return null;
  };

  const pollJob = async (jobId, runId) => {
    for (let i = 0; i < 90; i++) {
      await sleep(2500);
      if (runRef.current !== runId) return null;
      try {
        const r = await api.get(`/products/enhance-image/${jobId}`);
        if (r.data.status !== "processing") return r.data;
      } catch (err) {
        if (err.response?.status === 404) return { status: "failed", error: "Enhancement job not found" };
      }
    }
    return { status: "failed", error: "AI enhancement timed out — using your original photo." };
  };

  const enhanceImage = async (source, styleArg = bgStyle, hint = aiHint) => {
    const runId = ++runRef.current;
    setEnhancing(true);
    setShowCompare(false);
    try {
      const r = await api.post("/products/enhance-image", { ...source, style: styleArg, hint });
      const base = { id: r.data.id, original_url: r.data.original_url, enhanced_url: enhanced?.id === r.data.id ? enhanced.enhanced_url : null };
      setEnhanced(base);
      if (!base.enhanced_url) {
        setUsingEnhanced(false);
        setForm((f) => ({ ...f, images: [r.data.original_url] }));
      }
      const job = await pollJob(r.data.job_id, runId);
      if (!job) return;
      if (job.status === "done" && job.enhanced_url) {
        setEnhanced({ ...base, enhanced_url: job.enhanced_url });
        setUsingEnhanced(true);
        setForm((f) => ({ ...f, images: [job.enhanced_url] }));
        toast.success("Studio photo ready!", { description: "AI background · lighting · shadows · 1024×1024" });
      } else {
        toast.message("Using your original photo", { description: job.error || "AI enhancement skipped this time." });
      }
    } catch (err) {
      if (!source.image_id) setForm((f) => ({ ...f, images: [source.image_base64] }));
      toast.error(err.response?.data?.detail || "Enhancement failed — using original");
    }
    if (runRef.current === runId) setEnhancing(false);
  };

  const changeStyle = (id) => {
    setBgStyle(id);
    if (enhanced?.id && !enhancing) enhanceImage({ image_id: enhanced.id }, id);
  };

  const toggleVersion = (useEnhanced) => {
    if (!enhanced || (useEnhanced && !enhanced.enhanced_url)) return;
    setUsingEnhanced(useEnhanced);
    setForm((f) => ({ ...f, images: [useEnhanced ? enhanced.enhanced_url : enhanced.original_url] }));
  };

  const reset = () => {
    runRef.current += 1;
    setEnhancing(false);
    setAiHint("");
    setImgPreview(null);
    setImgBase64(null);
    setAiResult(null);
    setEnhanced(null);
    setForm((f) => ({ ...f, images: [] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) return toast.error("Name and price required");
    setSubmitting(true);
    try {
      await api.post("/products", {
        ...form,
        price: parseFloat(form.price),
        mrp: form.mrp ? parseFloat(form.mrp) : parseFloat(form.price),
        inventory: parseInt(form.inventory),
      });
      toast.success("Product added");
      nav("/admin/products");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
    setSubmitting(false);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Add new product</h1>
        <p className="text-gray-600 text-sm mt-1 flex items-center gap-1"><Sparkle weight="fill" className="text-[#F8CB46]" size={14} /> AI fills the form from a photo</p>
      </div>

      {/* Image upload area */}
      <div className="qd-card p-5">
        {imgPreview ? (
          <div>
            {/* Image display - shows enhanced or original */}
            <div className="relative">
              <div className="aspect-square sm:aspect-[16/9] rounded-xl bg-gray-50 overflow-hidden">
                {showCompare && enhanced?.enhanced_url ? (
                  <div className="grid grid-cols-2 h-full">
                    <div className="relative bg-gray-100">
                      <img src={enhanced.original_url} alt="Original" className="w-full h-full object-contain" />
                      <span className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full">Before</span>
                    </div>
                    <div className="relative bg-white border-l border-gray-200">
                      <img src={enhanced.enhanced_url} alt="Enhanced" className="w-full h-full object-contain" />
                      <span className="absolute top-2 left-2 bg-[#15803D] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full">After</span>
                    </div>
                  </div>
                ) : (
                  <img
                    src={enhanced ? (usingEnhanced && enhanced.enhanced_url ? enhanced.enhanced_url : enhanced.original_url) : imgPreview}
                    alt="" className="w-full h-full object-contain"
                    data-testid="add-product-image-preview"
                  />
                )}
              </div>

              <button onClick={reset}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 text-white flex items-center justify-center"
                data-testid="remove-image-btn">
                <X size={16} />
              </button>

              {enhancing && (
                <div className="absolute inset-0 bg-white/85 flex items-center justify-center rounded-xl" data-testid="enhancing-overlay">
                  <div className="text-center px-4">
                    <MagicWand weight="fill" size={36} className="text-[#15803D] animate-pulse mx-auto" />
                    <div className="mt-2 font-heading font-black">{analyzing ? "Reading your product…" : "Creating studio photo…"}</div>
                    <div className="text-xs text-gray-500">AI background · lighting · shadows — takes ~20–30s</div>
                  </div>
                </div>
              )}
            </div>

            <StylePicker value={bgStyle} onChange={changeStyle} disabled={enhancing || analyzing} />

            {/* Before/after controls */}
            {enhanced && !enhancing && (
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="enhance-controls">
                {enhanced.enhanced_url && (
                  <div className="inline-flex items-center gap-1 bg-[#15803D]/10 text-[#15803D] px-2 py-1 rounded-full text-xs font-bold" data-testid="enhanced-badge">
                    <CheckCircle weight="fill" size={12} /> Studio enhanced
                  </div>
                )}
                <div className="inline-flex rounded-full bg-gray-100 p-0.5">
                  <button onClick={() => toggleVersion(false)}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full transition ${!usingEnhanced ? "bg-white shadow" : "text-gray-600"}`}
                    data-testid="use-original-btn">
                    Use original
                  </button>
                  <button onClick={() => toggleVersion(true)} disabled={!enhanced.enhanced_url}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full transition ${usingEnhanced ? "bg-[#15803D] text-white shadow" : "text-gray-600"}`}
                    data-testid="use-enhanced-btn">
                    Use enhanced ✨
                  </button>
                </div>
                {enhanced.enhanced_url && <button onClick={() => setShowCompare((v) => !v)}
                  className="text-xs font-bold px-3 py-1.5 rounded-full border border-gray-200 hover:bg-gray-50 inline-flex items-center gap-1"
                  data-testid="compare-btn">
                  <ArrowsLeftRight size={12} weight="bold" />
                  {showCompare ? "Hide" : "Compare"} before/after
                </button>}
                <button onClick={() => enhanceImage({ image_id: enhanced.id })}
                  className="text-xs font-bold px-3 py-1.5 rounded-full border border-gray-200 hover:bg-gray-50 inline-flex items-center gap-1"
                  data-testid="reenhance-btn">
                  <ArrowsClockwise size={12} weight="bold" /> Re-run
                </button>
              </div>
            )}

            {aiResult && !analyzing && (
              <div className="mt-3 p-3 rounded-xl bg-[#F8CB46]/15 text-sm">
                <Sparkle weight="fill" className="inline text-[#15803D]" size={14} />{" "}
                {aiResult.ai_powered ? "AI-detected" : "Template-filled"}: <b>{aiResult.name}</b> · {aiResult.category}
              </div>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <button type="button"
              onClick={() => camRef.current?.click()}
              data-testid="open-camera-btn"
              className="border-2 border-dashed border-gray-200 rounded-2xl p-8 hover:border-[#F8CB46] hover:bg-[#F8CB46]/10 transition flex flex-col items-center gap-2">
              <Camera weight="duotone" size={36} className="text-[#15803D]" />
              <div className="font-bold">Open Camera</div>
              <div className="text-xs text-gray-500">Click product photo</div>
            </button>
            <button type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="upload-photo-btn"
              className="border-2 border-dashed border-gray-200 rounded-2xl p-8 hover:border-[#F8CB46] hover:bg-[#F8CB46]/10 transition flex flex-col items-center gap-2">
              <ImageIcon weight="duotone" size={36} className="text-[#15803D]" />
              <div className="font-bold">Upload Photo</div>
              <div className="text-xs text-gray-500">From your device</div>
            </button>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="file-input" />
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="camera-input" />
      </div>

      {/* Form */}
      <form onSubmit={submit} className="qd-card p-5 space-y-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Product name *</label>
          <input className="qd-input mt-2" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="prod-name-input" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Description</label>
          <textarea rows="3" className="qd-input mt-2" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="prod-desc-input" />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Selling price *</label>
            <input type="number" className="qd-input mt-2" value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              data-testid="prod-price-input" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">MRP</label>
            <input type="number" className="qd-input mt-2" value={form.mrp}
              onChange={(e) => setForm({ ...form, mrp: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Weight</label>
            <input className="qd-input mt-2" value={form.weight}
              onChange={(e) => setForm({ ...form, weight: e.target.value })}
              placeholder="500g" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Inventory</label>
            <input type="number" className="qd-input mt-2" value={form.inventory}
              onChange={(e) => setForm({ ...form, inventory: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Category</label>
            <select className="qd-input mt-2" value={form.category_id}
              onChange={(e) => setForm({ ...form, category_id: e.target.value })}
              data-testid="prod-category-select">
              <option value="">Select category</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        {form.tags.length > 0 && (
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">AI Tags</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {form.tags.map((t, i) => (
                <span key={i} className="px-3 py-1 rounded-full bg-gray-100 text-xs font-semibold">{t}</span>
              ))}
            </div>
          </div>
        )}
        <button type="submit" disabled={submitting}
          className="qd-btn-green w-full"
          data-testid="prod-submit-btn">
          {submitting ? "Adding…" : "Add product"}
        </button>
      </form>
    </div>
  );
}
